'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useVenueWebSocket } from '@/hooks/useVenueWebSocket';
import { useVenueActions } from '@/hooks/useVenueActions';
import { useAuth } from './AuthContext';
import { VENUE_OWNER_ROLE, STAFF_ROLE } from '@/constants/roles';
import { handleError } from '@/utils/errorHandler';
import { StaffVenue } from '@/server-side/types/staff';

interface WebSocketMessage {
  type: string;
  payload?: unknown;
  timestamp?: string;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

interface WebSocketContextType {
  connectionState: ConnectionState;
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  sendMessage: (message: WebSocketMessage) => void;
  subscribe: (venueIds: string[]) => void;
  unsubscribe: (venueIds: string[]) => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  connectionState: 'disconnected',
  isConnected: false,
  lastMessage: null,
  sendMessage: () => {},
  subscribe: () => {},
  unsubscribe: () => {},
});

export const useWebSocket = () => useContext(WebSocketContext);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const { getUserVenues } = useVenueActions();
  const [venueIds, setVenueIds] = useState<string[]>([]);
  const [messageHandlers, setMessageHandlers] = useState<
    ((message: WebSocketMessage) => void)[]
  >([]);

  // Determine if user should have WebSocket connection (venue_owner or staff only)
  const isVenueUser =
    isAuthenticated() &&
    user?.roles?.some(
      (role) => role === VENUE_OWNER_ROLE || role === STAFF_ROLE
    );

  // Fetch venue IDs when user is authenticated as venue user
  useEffect(() => {
    if (!isVenueUser || !user?.sub) {
      setVenueIds([]);
      return;
    }

    const userId = user.sub;

    const fetchVenueIds = async () => {
      await getUserVenues(userId, {
        onSuccess: (data: StaffVenue[]) => {
          const ids = data.map((v) => v.venueId);
          setVenueIds(ids);
        },
        onError: (error) => {
          handleError(error, {
            component: 'WebSocketContext',
            action: 'getUserVenues',
          });
        },
      });
    };

    fetchVenueIds();
  }, [isVenueUser, user?.sub]);

  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      // Call all registered message handlers
      messageHandlers.forEach((handler) => handler(message));
    },
    [messageHandlers]
  );

  const {
    connectionState,
    isConnected,
    lastMessage,
    sendMessage,
    subscribe,
    unsubscribe,
  } = useVenueWebSocket({
    enabled: isVenueUser ?? false,
    venueIds,
    onMessage: handleMessage,
  });

  const value: WebSocketContextType = {
    connectionState,
    isConnected,
    lastMessage,
    sendMessage,
    subscribe,
    unsubscribe,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
